<script setup lang="ts">
import {autoUpdate, computePosition, offset} from '@floating-ui/vue';

const props = defineProps({
  label: {
    type: String,
    default: null,
  },
  modelValue: {
    type: [String, Object],
    default: '',
  },
  id: {
    type: String,
  },
  type: {
    type: String,
    default: 'text',
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: false,
  },
  hint: {
    type: [String, Array, undefined],
    default: undefined,
  },
  invalid: {
    type: Boolean,
    default: false,
  },
  selectOptions: {
    type: Array as PropType<Array<any>>,
    default: () => []
  },
  selectOptionsKey: {
    type: String,
    default: 'id'
  },
  selectOptionsLabel: {
    type: String,
    default: 'name'
  },
  showClear: {
    type: Boolean,
    default: false,
  },
  minHeight: {
    type: String,
    default: '7.5rem'
  },
  selectPlacement: {
    type: String,
    default: 'bottom-start'
  }
});

const computedId = ref(props.id || null);

onMounted(() => {
  if (!computedId.value) {
    computedId.value = `${(props.label || 'input').replace(/\s+/g, '-').toLowerCase()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  if(props.type === 'select') {
    document.addEventListener('click', handleClickOutside);
  }
});

const emit = defineEmits(['update:modelValue']);

function updateValue(value) {
  emit('update:modelValue', value);
}

//region Select
const selectContainerRef = ref<HTMLElement | null>(null);
const selectInputRef = ref<HTMLElement | null>(null);
const selectOptionsRef = ref<HTMLElement | null>(null);
const floatingStyles = ref({})
let cleanupAutoUpdate = undefined;
watchEffect(async () => {
  if (selectInputRef.value && selectOptionsRef.value) {
    await nextTick();
    cleanupAutoUpdate?.();

    cleanupAutoUpdate = autoUpdate(
        selectInputRef.value,
        selectOptionsRef.value,
        async () => {
          const { x, y } = await computePosition(selectInputRef.value, selectOptionsRef.value, {
            placement: props.selectPlacement as any,
            strategy: 'fixed',
            middleware: [offset(4)]
          })
          floatingStyles.value = {
            left: `${x}px`,
            top: `${y}px`,
            position: 'fixed',
            width: `${selectInputRef.value.offsetWidth}px`
          }
        }
    )
  }
})
onUnmounted(() => {
  cleanupAutoUpdate?.();
  if(props.type === 'select') {
    document.removeEventListener('click', handleClickOutside);
  }
})

const showOptions = ref(false);
const toggleShowOptions = () => {
  if(showOptions.value){
    cleanupAutoUpdate?.();
  }
  showOptions.value = !showOptions.value;
}
const selectSearch = ref<string|null>(null);
const filteredSelectOptions = computed(() => {
  return props.selectOptions.filter(option => {
    if(selectSearch.value){
      return option[props.selectOptionsLabel].toLowerCase().includes(selectSearch.value.toLowerCase());
    } else {
      return true;
    }
  });
})
const onSelectOptionClick = (option) => {
  if(props.modelValue && props.modelValue[props.selectOptionsKey] === option[props.selectOptionsKey]) {
    updateValue(null);
  } else {
    updateValue(option);
  }
  toggleShowOptions();
}
const handleClickOutside = (event: MouseEvent) => {
  if(showOptions.value && selectContainerRef.value && !selectContainerRef.value.contains(event.target as HTMLElement)) {
    toggleShowOptions();
  }
}



//endregion

</script>

<template>
  <div class="wad4-input-group" :class="{ 'wad4-input-group--invalid': invalid }">

    <label class="wad4-input-group__label" :for="computedId" v-if="label">
      <span>{{ label }}</span>
      <span class="wad4-input-group__label-required">{{ required ? '*' : '' }}</span>
    </label>

    <div v-if="type !== 'textarea' && type !== 'select'" class="wad4-input-group__input_container">
      <input class="wad4-input-group__input"
             :class="{'wad4-input-group__input--with-clear' : showClear}"
             :id="computedId" :type="type" :placeholder="placeholder" :value="modelValue" :disabled="disabled"
             :required="required" @input="updateValue($event.target.value)" />
      <svg v-if="showClear" @click="updateValue('')" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="var(--wad4-c-border-border-disabled)"></path>
      </svg>
    </div>

    <textarea v-if="type === 'textarea'" class="wad4-input-group__input wad4-input-group__textarea" :style="{'min-height': minHeight}"
          :id="computedId" :placeholder="placeholder" :disabled="disabled" :required="required" :value="modelValue"
          @input="updateValue($event.target.value)"/>

    <div v-if="type === 'select'" class="wad4-input-group__select" ref="selectContainerRef">
      <input :id="computedId" ref="selectInputRef" type="text" :placeholder="placeholder" :disabled="disabled" v-model="selectSearch"
          class="wad4-input-group__input wad4-input-group__input--select"
          @click="showOptions = true"/>
      <svg @click="toggleShowOptions" class="wad4-input-group__select-icon" xmlns="http://www.w3.org/2000/svg" height="24px" width="24px" viewBox="0 -960 960 960">
        <path v-show="!showOptions" d="M480-360 280-560h400L480-360Z"/>
        <path v-show="showOptions" d="m280-400 200-200 200 200H280Z"/>
      </svg>
      <ul v-if="showOptions" ref="selectOptionsRef" class="wad4-input-group__select-options" :style="floatingStyles">
        <li v-for="option in filteredSelectOptions" class="wad4-input-group__select-options-item"
            :class="{'wad4-input-group__select-options-item--selected': modelValue && modelValue[selectOptionsKey] === option[selectOptionsKey]}"
            @click="onSelectOptionClick(option)">
          {{option.name}}
        </li>
        <li v-show="selectSearch && filteredSelectOptions.length === 0"
            class="wad4-input-group__select-options-item wad4-input-group__select-options-item--no-results">
          No results for your search
        </li>
      </ul>
    </div>
    <div v-if="hint && !Array.isArray(hint)" class="wad4-input-group__hint" v-html="hint"></div>
    <div v-else-if="hint" class="wad4-input-group__hint">
      <span v-for="token in hint">
        <a v-if="token.isLink" href="#" @click.prevent="contactSupport">{{ token.text }}</a>
        <span v-else>{{ token.text }}</span>
      </span>
    </div>

  </div>
</template>

<style lang="scss">
.wad4-input-group {
  display: flex;
  flex-direction: column;
  gap: $wad4-spacing-md;

  &__label {
    @include text-size-mixin('xs');
    font-weight: $wad4-font-weight-semi-bold;
    display: flex;
    align-items: flex-start;
    gap: $wad4-spacing-xxs;
    color: $wad4-c-text-text-secondary;

    &-required {
      color: $wad4-c-foreground-fg-brand-solid;
    }
  }

  &__select {
    position: relative;
    width: 100%;

    &-icon {
      position: absolute;
      right: $wad4-spacing-lg;
      top:$wad4-spacing-md;

      & path {
        fill: $wad4-c-text-text-tertiery;
      }
    }

    &-options {
      background: $wad4-c-background-bg-primary;
      list-style: none;
      padding: $wad4-spacing-md $wad4-spacing-lg;
      margin: 0;
      max-height: calc(200px + (2 * $wad4-spacing-md));
      overflow-y: scroll;
      z-index: 1003;
      border: solid 1px $wad4-c-border-border-primary;
      box-shadow: $wad4-box-shadow-sm;
      border-radius: $wad4-border-radius-xs;

      &-item {
        color: $wad4-c-text-text-primary;
        overflow: hidden;
        text-overflow: ellipsis;
        @include text-size-mixin('md');
        padding: $wad4-spacing-md;
        cursor: default;

        @include hover {
          background: $wad4-c-background-bg-tertiery;
        }

        &--selected {
          background: $wad4-c-background-bg-brand-solid;
          color: $wad4-c-text-text-primary-on-brand;

          @include hover {
            background: $wad4-c-background-bg-brand-solid;
          }
        }

        &--no-results {
          font-style: italic;
          color: $wad4-c-text-text-disabled;

          @include hover {
            background: none;
          }
        }
      }
    }
  }

  &__input {
    border: 1px solid $wad4-c-border-border-primary;
    border-radius: $wad4-border-radius-xs;
    background: $wad4-c-background-bg-primary;
    box-shadow: $wad4-box-shadow-xs;
    color: $wad4-c-text-text-primary;
    // 16px, never smaller: iOS Safari zooms the page when a smaller field gets focus.
    @include text-size-mixin('lg');
    padding: $wad4-spacing-md $wad4-spacing-lg;
    width: 100%;

    &--select {
      padding-right: calc($wad4-spacing-lg + 20px);
    }

    &:focus {
      outline: 2px solid $wad4-c-border-border-brand;
      outline-offset: -2px;
    }

    &:disabled {
      background: $wad4-c-background-bg-disabled;
      color: $wad4-c-text-text-disabled;
    }

    // Chrome paints its own pale blue over an autofilled field, which on the dark theme
    // renders as a white box with black text in the middle of a dark card. `background`
    // cannot override it, so the fill is faked with a large inset shadow (the design's
    // xs shadow is kept in front of it) and the text recoloured through
    // -webkit-text-fill-color. The absurd transition delay is the standard trick to stop
    // Chrome re-painting its colour on focus.
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-text-fill-color: $wad4-c-text-text-primary;
      -webkit-box-shadow: $wad4-box-shadow-xs, 0 0 0 1000px $wad4-c-background-bg-primary inset;
      box-shadow: $wad4-box-shadow-xs, 0 0 0 1000px $wad4-c-background-bg-primary inset;
      caret-color: $wad4-c-text-text-primary;
      transition: background-color 100000s ease-in-out 0s;
    }

    &--with-clear {
      padding: $wad4-spacing-md $wad4-spacing-5xl $wad4-spacing-md $wad4-spacing-lg;
    }

    &_container {
      position: relative;
      svg {
        cursor: pointer;
        position: absolute;
        top: 9px;
        right: $wad4-spacing-lg;
      }
    }
  }

  &__textarea{
    resize: vertical;
    font-family: $wad4-default-font-sans;
  }

  &__hint {
    @include text-size-mixin('xs');
    color: $wad4-c-text-text-tertiery;
  }

  &--invalid {
    .wad4-input-group__input {
      outline: 2px solid $wad4-c-border-border-error;
      outline-offset: -2px;
    }

    .wad4-input-group__hint {
      color: $wad4-c-text-text-error;
    }
  }
}
</style>
